import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface ReauthDialogProps {
  onReauthenticate: (password: string) => Promise<void>;
  actionText: string;
  onMFACode?: (code: string) => Promise<void>;
  phoneNumber?: string;
  showMFA?: boolean;
}

export const ReauthDialog = ({
  onReauthenticate,
  actionText,
  onMFACode,
  phoneNumber,
  showMFA
}: ReauthDialogProps) => {
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const inputId = useId();

  const handleSubmit = async () => {
    if (showMFA && onMFACode) {
      setIsVerifying(true);
      try {
        await onMFACode(verificationCode);
      } finally {
        setIsVerifying(false);
      }
    } else {
      await onReauthenticate(password);
    }
    setPassword("");
    setVerificationCode("");
  };

  return (
    <div className="mt-8 flex w-full flex-col gap-6">
      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-base font-medium text-fg">Confirm It’s You</span>
        <span className="text-sm text-fg-3">
          {!showMFA
            ? `Please enter your password to ${actionText}.`
            : `Please enter the verification code sent to ${phoneNumber}.`}
        </span>
      </div>
      {!showMFA ? (
        <div className="flex w-full flex-col gap-1.5">
          <Label htmlFor={inputId}>Password</Label>
          <Input
            id={inputId}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-1.5">
          <Label htmlFor={inputId}>Verification Code</Label>
          <Input
            id={inputId}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter verification code"
          />
        </div>
      )}
      <div className="flex w-full justify-end">
        <Button variant="primary" disabled={isVerifying} onClick={handleSubmit}>
          {isVerifying ? <Spinner /> : null}
          Confirm
        </Button>
      </div>
    </div>
  );
};
